import { InputRule, mergeAttributes, Node, nodePasteRule, type PasteRule } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { escapeRegExp } from "lodash-es";

import { EditorEmoji } from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EditorEmoji";
import {
    emojiDictionary,
    emojisByFriendlyName,
    type EmojiTypes,
} from "commonComponents/btWrappers/editor/core/extensions/custom/emoji/EmojiMappings";

export interface IEmojiOptions {
    HTMLAttributes?: Record<string, any>;
}

declare module "@tiptap/core" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Commands<ReturnType> {
        emoji: {
            /**
             * Insert an emoji
             */
            insertEmoji: (emojiName: EmojiTypes) => ReturnType;
        };
    }
}

export const Emoji = Node.create<IEmojiOptions>({
    name: "emoji",
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    // this ensures that extensions like textStyle renders first and then the emoji renders within it
    priority: 49,

    addOptions() {
        return {
            HTMLAttributes: undefined,
        };
    },

    addAttributes() {
        return {
            "data-emojitype": {
                default: "smile",
                parseHTML: (element) => element.getAttribute("data-emojitype"),
                renderHTML: (elementAttributes) => {
                    return {
                        "data-emojitype": elementAttributes["data-emojitype"],
                    };
                },
            },
            "data-emojitext": {
                default: emojiDictionary["smile"].unicode,
                parseHTML: (element) => element.getAttribute("data-emojitext"),
                renderHTML: (elementAttributes) => {
                    return {
                        "data-emojitext": elementAttributes["data-emojitext"],
                    };
                },
            },
        };
    },
    addInputRules() {
        const rules: InputRule[] = [];
        Object.values(emojiDictionary).forEach((emoji) => {
            emoji.shortcodes.forEach((shortcode) => {
                rules.push(
                    new InputRule({
                        find: new RegExp(`(?:\\s|^)(${escapeRegExp(shortcode)})(?:\\s)`, "gu"),
                        handler: ({ range, match, chain }) => {
                            const emojiKey = Object.keys(emojiDictionary).find(
                                (key) =>
                                    emojiDictionary[key as keyof typeof emojiDictionary] === emoji,
                            );
                            chain()
                                .insertEmoji(emojiKey as EmojiTypes)
                                .insertContent(" ")
                                .command(({ tr }) => {
                                    const firstChar = match[0][0];
                                    if (firstChar === " ") {
                                        tr.insertText(firstChar, range.from, range.to);
                                    } else {
                                        tr.deleteRange(range.from, range.to);
                                    }
                                    return true;
                                });
                        },
                    }),
                );
            });
        });
        return rules;
    },
    addNodeView() {
        return ReactNodeViewRenderer(EditorEmoji);
    },
    renderHTML({ HTMLAttributes, node }) {
        const emojiType = HTMLAttributes["data-emojitype"] as string;
        const emoji: (typeof emojisByFriendlyName)[string] = emojisByFriendlyName[emojiType];
        return [
            "span",
            mergeAttributes(this.options.HTMLAttributes ?? {}, HTMLAttributes, node.attrs),
            `${emoji.unicode}`,
        ];
    },
    parseHTML() {
        return Object.values(emojiDictionary).map((e) => {
            return {
                tag: `span[data-emojitype="${e.friendlyName}"]`,
            };
        });
    },
    addPasteRules() {
        const rules: PasteRule[] = [];
        Object.values(emojiDictionary).forEach((e) => {
            rules.push(
                nodePasteRule({
                    find: new RegExp(e.unicode, "gu"),
                    type: this.type,
                    getAttributes: {
                        "data-emojitype": e.friendlyName,
                        "data-emojitext": e.unicode,
                    },
                }),
                ...e.shortcodes.map((sc) => {
                    return nodePasteRule({
                        find: new RegExp(`${escapeRegExp(sc)}`, "gu"),
                        type: this.type,
                        getAttributes: {
                            "data-emojitype": e.friendlyName,
                            "data-emojitext": e.unicode,
                        },
                    });
                }),
            );
        });
        return rules;
    },
    addCommands() {
        return {
            insertEmoji:
                (emojiKey) =>
                ({ chain, can, dispatch }) => {
                    const emoji = emojiDictionary[emojiKey];
                    if (dispatch) {
                        return chain()
                            .command((props) => {
                                const attributes = {
                                    "data-emojitype": emoji.friendlyName,
                                    "data-emojitext": emoji.unicode,
                                };
                                const { splittableMarks } = this.editor.extensionManager;
                                const { selection, storedMarks } = props.state;
                                const marks = storedMarks || selection.$from.marks();
                                const filteredMarks = marks.filter(
                                    (mark) =>
                                        splittableMarks.includes(mark.type.name) &&
                                        this.type.markSet?.includes(mark.type),
                                );
                                const emojiNode = this.type.create(attributes).mark(filteredMarks);

                                props.tr.replaceWith(
                                    props.tr.selection.$from.pos,
                                    props.tr.selection.$to.pos,
                                    emojiNode,
                                );

                                return true;
                            })
                            .run();
                    }
                    return can()
                        .chain()
                        .insertContent({
                            type: this.type.name,
                            attrs: {
                                "data-emojitype": emoji.friendlyName,
                                "data-emojitext": emoji.unicode,
                            },
                        })
                        .run();
                },
        };
    },
});

export default Emoji;
